import { Duration } from "luxon";

import * as Filters from "@app/analytics/meetops/filters";
import { Filter, MeetOpsQuery } from "@app/analytics/meetops/types";
import { PartialBy } from "@app/common/types";

type MetricFormatType = "percent" | "number" | "index" | "score" | "string";
type MetricFormatFunction = (value: number | null) => string;
type MetricValueFunction = (rawValue: number | null) => number | null;

export interface SimpleMetricMeta {
  id: string;
  queryName: string;
  displayName: string;
  getFilters: () => MeetOpsQuery["filters"];
  toFormat: MetricFormatFunction;
  toValue: MetricValueFunction;
  formatType: MetricFormatType;
  computed?: null;
  trend: TrendMeta;
}

export interface ParsedMetric {
  id: string;
  value: number | null;
  rawValue: number | null;
  formattedValue: string;
}

export interface ComputedMetricMeta {
  id: string;
  displayName: string;
  toFormat: MetricFormatFunction;
  toValue: MetricValueFunction;
  formatType: MetricFormatType;
  getMetrics: () => SimpleMetricMeta[];
  getComputedValue: (metrics: { [key: string]: ParsedMetric }) => number | null;
  trend: TrendMeta;
  computed: true;
}

export type MetricMeta = SimpleMetricMeta | ComputedMetricMeta;

export type TrendMeta = 0 | 1;
export const TrendMeta = {
  UP_POSITIVE: 0 as const,
  UP_NEGATIVE: 1 as const,
};

const createMetricMeta = (
  metricMetaConfig: PartialBy<
    SimpleMetricMeta,
    "getFilters" | "trend" | "toValue" | "toFormat" | "formatType"
  >
): SimpleMetricMeta => {
  return {
    getFilters: () => [],
    toFormat: stringFormat,
    toValue: (rawValue) => (rawValue != null ? Math.round(rawValue) : null),
    trend: TrendMeta.UP_POSITIVE,
    formatType: "number",
    ...metricMetaConfig,
  };
};

const nullFormat =
  (toFormat: MetricFormatFunction) => (value: number | null) =>
    value != null ? toFormat(value) : "-";
const percentFormat: MetricFormatFunction = nullFormat((value) => `${value}%`);
const stringFormat: MetricFormatFunction = nullFormat((value) => `${value}`);
const timeFormat: MetricFormatFunction = (value) =>
  value == null ? "0:00" : Duration.fromMillis(value * 1000).toFormat("m:ss");

const percentValue = (rawValue: number | null) =>
  rawValue == null ? null : Math.round(rawValue * 100);

const meetingCount: SimpleMetricMeta = createMetricMeta({
  id: "meetingCount",
  queryName: "count_distinct:session_id",
  displayName: "Total Meetings",
});

const attendedMeetingCount: SimpleMetricMeta = createMetricMeta({
  id: "attendedMeetingCount",
  queryName: "count_distinct:session_id",
  displayName: "Attended Meetings",
  getFilters: () => [Filters.wasInMeeting()],
});

const observedParticipantCount: SimpleMetricMeta = createMetricMeta({
  id: "observedParticipantCount",
  queryName: "average:s_observed_participant_count",
  displayName: "Participants",
  toFormat: stringFormat,
});

const oneOnOneHoursInMeeting: SimpleMetricMeta = createMetricMeta({
  id: "oneOnOneHoursInMeeting",
  queryName: "sum:p_time_in_meeting",
  displayName: "Total 1:1 Meetings",
  toValue: (rawValue) => (rawValue ? Math.round(rawValue / 360) / 10 : 0), // convert seconds to hours
  getFilters: () => [
    {
      name: "s_observed_participant_count",
      operator: "equal",
      comparisonValue: 2,
    },
  ],
});

const groupHoursInMeeting: SimpleMetricMeta = createMetricMeta({
  id: "groupHoursInMeeting",
  queryName: "sum:p_time_in_meeting",
  displayName: "Total Group Meetings",
  toValue: (rawValue) => (rawValue ? Math.round(rawValue / 360) / 10 : 0), // convert seconds to hours
  getFilters: () => [
    {
      name: "s_observed_participant_count",
      operator: "greater_than",
      comparisonValue: 2,
    },
  ],
});

const cameraOffPercent: SimpleMetricMeta = createMetricMeta({
  id: "cameraOffPercent",
  queryName: "average:p_camera_on_ratio",
  displayName: "Camera Off",
  toFormat: percentFormat,
  toValue: (rawValue) =>
    rawValue == null ? null : Math.round((1 - rawValue) * 100),
  formatType: "percent",
  trend: TrendMeta.UP_NEGATIVE,
});

const cameraOnPercent: SimpleMetricMeta = createMetricMeta({
  id: "cameraOnPercent",
  queryName: "average:p_camera_on_ratio",
  displayName: "Camera On",
  toFormat: percentFormat,
  toValue: percentValue,
  formatType: "percent",
});

const microphoneOffPercent: SimpleMetricMeta = createMetricMeta({
  id: "microphoneOffPercent",
  queryName: "average:p_microphone_on_ratio",
  displayName: "Mic Off",
  toFormat: percentFormat,
  toValue: (rawValue) =>
    rawValue == null ? null : Math.round((1 - rawValue) * 100),
  formatType: "percent",
  trend: TrendMeta.UP_NEGATIVE,
});

const microphoneOnPercent: SimpleMetricMeta = createMetricMeta({
  id: "microphoneOnPercent",
  queryName: "average:p_microphone_on_ratio",
  displayName: "Mic On",
  toFormat: percentFormat,
  toValue: percentValue,
  formatType: "percent",
});

const presentationOnPercent: SimpleMetricMeta = createMetricMeta({
  id: "presentationOnPercent",
  queryName: "average:p_presentation_on_ratio",
  displayName: "Screen Share",
  toFormat: percentFormat,
  toValue: percentValue,
  formatType: "percent",
});

const talkTimePercent: SimpleMetricMeta = createMetricMeta({
  id: "talkTimePercent",
  queryName: "average:p_talk_time_ratio",
  displayName: "Talk Time",
  toFormat: percentFormat,
  // round talk time to the nearest tenth
  toValue: (rawValue) =>
    rawValue == null ? null : Math.round(rawValue * 1000) / 10,
  formatType: "percent",
});

const participantTalkTime: SimpleMetricMeta = createMetricMeta({
  id: "participantTalkTime",
  queryName: "average:p_talk_time",
  displayName: "Talk Time (Measured)",
  toFormat: (value) =>
    value == null ? "0:00" : Duration.fromMillis(value * 1000).toFormat("m:ss"),
});

const participantOnTimeFilter: Filter = {
  name: "p_on_time",
  operator: "equal",
  comparisonValue: "True",
};

const participantLateFilter: Filter = {
  name: "p_on_time",
  operator: "equal",
  comparisonValue: "False",
};

const meetingLateFilter: Filter = {
  name: "s_on_time",
  operator: "equal",
  comparisonValue: "False",
};

const meetingWithStartTimeFilter: Filter = {
  name: "s_scheduled_start_time",
  operator: "not_null",
  comparisonValue: null,
};

const meetingStartedLateFilter: Filter = {
  name: "s_on_time",
  operator: "equal",
  comparisonValue: "False",
};

const meetingWithScheduledDurationFilter: Filter = {
  name: "s_scheduled_duration",
  operator: "not_null",
  comparisonValue: null,
};

const participantOnTimeMeetingCount: SimpleMetricMeta = createMetricMeta({
  ...meetingCount,
  id: "participantOnTimeMeetingCount",
  displayName: "Total On Time Meetings",
  getFilters: () => [participantOnTimeFilter],
});

const participantJoinedLateMeetingCount: SimpleMetricMeta = createMetricMeta({
  ...meetingCount,
  id: "participantLateToMeetingCount",
  displayName: "Total Meetings Joined Late",
  getFilters: () => [participantLateFilter],
});

const meetingWithStartTimeCount: SimpleMetricMeta = createMetricMeta({
  ...meetingCount,
  id: "meetingWithStartTimeCount",
  displayName: "Total Meetings with Scheduled Start Time",
  getFilters: () => [meetingWithStartTimeFilter],
});

const meetingStartedLateCount: SimpleMetricMeta = createMetricMeta({
  ...meetingCount,
  id: "meetingStartedLateCount",
  displayName: "Total Meetings Started Late by Host",
  getFilters: () => [meetingStartedLateFilter],
});

const meetingWithScheduledDurationCount: SimpleMetricMeta = createMetricMeta({
  ...meetingCount,
  id: "meetingWithScheduledDurationCount",
  displayName: "Total Meetings with Scheduled Duration",
  getFilters: () => [meetingWithScheduledDurationFilter],
});

const participantLatePercent: ComputedMetricMeta = {
  id: "participantLatePercent",
  displayName: "Late Arrival",
  getMetrics: () => [participantOnTimeMeetingCount, meetingWithStartTimeCount],
  formatType: "percent",
  toFormat: percentFormat,
  toValue: percentValue,
  getComputedValue: (metrics) => {
    const meetingWithStartTimeCount =
      metrics.meetingWithStartTimeCount?.rawValue;

    const onTimeMeetings = metrics.participantOnTimeMeetingCount?.rawValue;
    if (
      meetingWithStartTimeCount == null ||
      meetingWithStartTimeCount == 0 ||
      onTimeMeetings == null
    ) {
      return null;
    }

    return (
      (meetingWithStartTimeCount - onTimeMeetings) / meetingWithStartTimeCount
    );
  },
  computed: true,
  trend: TrendMeta.UP_NEGATIVE,
};

const meetingSizeBucket: ComputedMetricMeta = {
  id: "meetingSizeBucket",
  displayName: "Meeting Size",
  getMetrics: () => [observedParticipantCount],
  formatType: "string",
  toFormat: (r) => {
    if (r === 0) return "small";
    if (r === 1) return "medium";
    if (r === 2) return "large";
    else return "-";
  },
  toValue: (rawValue) => rawValue,
  getComputedValue: (r) => {
    if (r.observedParticipantCount!.value! === 2) return 0;
    if (
      r.observedParticipantCount!.value! > 2 &&
      r.observedParticipantCount!.value! <= 7
    )
      return 1;
    if (r.observedParticipantCount!.value! > 7) return 2;
    else return 0;
  },
  computed: true,
  trend: TrendMeta.UP_NEGATIVE,
};

const meetingLateStartPercent: ComputedMetricMeta = {
  id: "meetingLateStartPercent",
  displayName: "Late Start",
  getMetrics: () => [meetingStartedLateCount, meetingWithStartTimeCount],
  formatType: "percent",
  toFormat: percentFormat,
  toValue: percentValue,
  getComputedValue: (metrics) => {
    const meetingWithStartTimeCount =
      metrics.meetingWithStartTimeCount?.rawValue;
    const meetingStartedLateCount = metrics.meetingStartedLateCount?.rawValue;

    if (!meetingWithStartTimeCount || meetingStartedLateCount == null) {
      return null;
    }

    return meetingStartedLateCount / meetingWithStartTimeCount;
  },
  computed: true,
  trend: TrendMeta.UP_NEGATIVE,
};

const participantHoursInMeeting: ComputedMetricMeta = {
  id: "participantHoursInMeeting",
  displayName: "Hours in Meeting",
  getMetrics: () => [participantTimeInMeeting],
  formatType: "number",
  toFormat: stringFormat,
  toValue: (value) => (value == null ? null : Math.round(value * 10) / 10),
  getComputedValue: (metrics) => {
    const meetingSeconds = metrics.participantTimeInMeeting?.rawValue;
    if (meetingSeconds == null) {
      return null;
    }
    return meetingSeconds / 3600;
  },
  computed: true,
  trend: TrendMeta.UP_NEGATIVE,
};

const participantMissedMeetings: SimpleMetricMeta = createMetricMeta({
  id: "participantMissedMeetings",
  queryName: "count_distinct:session_id",
  displayName: "Missed Meetings",
  toValue: (rawValue) => rawValue,
  toFormat: stringFormat,
  getFilters: () => [Filters.missedMeeting()],
});

const participantMissedMeetingsPercent: ComputedMetricMeta = {
  id: "participantMissedMeetingsPercent",
  displayName: "Missed Meetings",
  getMetrics: () => [participantMissedMeetings, meetingCount],
  formatType: "percent",
  toFormat: percentFormat,
  toValue: (value) => (value == null ? null : Math.round(value)),
  getComputedValue: (metrics) => {
    const meetingCount = metrics.meetingCount?.rawValue;
    const missedMeetings = metrics.participantMissedMeetings?.rawValue ?? 0;

    if (!meetingCount) return null;

    return (missedMeetings / meetingCount) * 100;
  },
  computed: true,
  trend: TrendMeta.UP_NEGATIVE,
};

const participantParticipation = createMetricMeta({
  id: "participantParticipation",
  displayName: "Participation",
  queryName: "average:p_participation",
  toFormat: percentFormat,
  toValue: percentValue,
  formatType: "percent",
});

const participantMeetingScore: SimpleMetricMeta = createMetricMeta({
  id: "participantMeetingScore",
  queryName: "average:p_meeting_score",
  displayName: "Read Score",
});

const participantEngagement: SimpleMetricMeta = createMetricMeta({
  id: "participantEngagement",
  queryName: "average:p_engagement_score",
  displayName: "Engagement",
});

const participantObservedJoinTime: SimpleMetricMeta = createMetricMeta({
  id: "participantObservedJoinTime",
  queryName: "minimum:p_first_observed_time",
  displayName: "Join Time",
});

const participantSentiment: SimpleMetricMeta = createMetricMeta({
  id: "participantSentiment",
  queryName: "average:p_sentiment_score",
  displayName: "Sentiment",
});

const participantTimeInMeeting: SimpleMetricMeta = createMetricMeta({
  id: "participantTimeInMeeting",
  queryName: "sum:p_time_in_meeting",
  displayName: "Time in Meeting",
});

const avgParticipantTimeInMeeting: SimpleMetricMeta = createMetricMeta({
  id: "avgParticipantTimeInMeeting",
  queryName: "average:p_time_in_meeting",
  displayName: "Time in Meeting",
});

const participantCharisma: SimpleMetricMeta = createMetricMeta({
  id: "participantCharisma",
  queryName: "average:p_charisma",
  displayName: "Charisma",
});

const participantBias: SimpleMetricMeta = createMetricMeta({
  id: "participantBias",
  queryName: "average:p_bias",
  displayName: "Bias",
});

const participantLateBy: SimpleMetricMeta = createMetricMeta({
  id: "participantLateBy",
  queryName: "sum:p_late_by",
  displayName: "Late By",
  toFormat: timeFormat,
  trend: TrendMeta.UP_NEGATIVE,
  getFilters: () => [participantLateFilter],
});

const avgParticipantLateBy: SimpleMetricMeta = createMetricMeta({
  id: "avgParticipantLateBy",
  queryName: "average:p_late_by",
  displayName: "Average Participant Late By (Mins)",
  toFormat: timeFormat,
  trend: TrendMeta.UP_NEGATIVE,
  getFilters: () => [participantLateFilter],
});

const avgMeetingLateBy: SimpleMetricMeta = createMetricMeta({
  id: "avgMeetingLateBy",
  queryName: "average:s_late_by",
  displayName: "Average Meeting Late Start (Mins)",
  toFormat: timeFormat,
  trend: TrendMeta.UP_NEGATIVE,
  getFilters: () => [meetingLateFilter],
});

const participantAttendance = createMetricMeta({
  id: "participantAttendance",
  displayName: "Attendance",
  queryName: "sum:p_time_in_meeting",
  toFormat: (value) => (value == null || value == 0 ? "Absent" : "Present"),
  toValue: (rawValue) => rawValue,
});

const meetingCost: SimpleMetricMeta = createMetricMeta({
  id: "meetingCost",
  queryName: "sum:p_meeting_cost",
  displayName: "Meeting Cost",
});

const meetingDurationPlanned = createMetricMeta({
  id: "meetingDurationPlanned",
  queryName: "average:s_scheduled_duration",
  displayName: "Meeting Duration (Planned)",
  toFormat: (value) => (value == null ? "-" : `${value} min`),
  toValue: (rawValue) => (rawValue == null ? null : Math.round(rawValue / 60)),
});

const meetingDurationActual = createMetricMeta({
  id: "meetingDurationActual",
  queryName: "average:s_observed_duration",
  displayName: "Meeting Duration (Actual)",
  toFormat: (value) => (value == null ? "-" : `${value} min`),
  toValue: (rawValue) => (rawValue == null ? null : Math.round(rawValue / 60)),
});

const numInstancesBySession = createMetricMeta({
  id: "numInstancesBySession",
  queryName: "count_distinct:session_id",
  displayName: "Instances per Session",
});

const meetingScore: SimpleMetricMeta = createMetricMeta({
  id: "meetingScore",
  queryName: "average:s_meeting_score",
  displayName: "Read Score",
});

const sentiment: SimpleMetricMeta = createMetricMeta({
  id: "sentiment",
  queryName: "average:s_sentiment_score",
  displayName: "Sentiment",
});

const engagement: SimpleMetricMeta = createMetricMeta({
  id: "engagement",
  queryName: "average:s_engagement_score",
  displayName: "Engagement",
});

const onTimeRate: SimpleMetricMeta = createMetricMeta({
  id: "onTimeRate",
  queryName: "average:s_on_time_ratio",
  displayName: "On-Time Rate",
  toFormat: percentFormat,
  toValue: percentValue,
  formatType: "percent",
});

const attendanceRate: SimpleMetricMeta = createMetricMeta({
  id: "attendanceRate",
  queryName: "average:s_attendance_ratio",
  displayName: "Attendance Rate",
  toFormat: percentFormat,
  toValue: percentValue,
  formatType: "percent",
});

const meetingTalkTimeEntropy: SimpleMetricMeta = createMetricMeta({
  id: "meetingTalkTimeEntropy",
  queryName: "average:s_talk_time_entropy",
  displayName: "Talk Time Entropy",
  toFormat: percentFormat,
  toValue: percentValue,
  formatType: "percent",
});

const METRIC_META_BY_ID = {
  attendedMeetingCount,
  avgParticipantTimeInMeeting,
  meetingCount,
  meetingCost,
  oneOnOneHoursInMeeting,
  groupHoursInMeeting,
  presentationOnPercent,
  cameraOnPercent,
  cameraOffPercent,
  microphoneOffPercent,
  microphoneOnPercent,
  participantOnTimeMeetingCount,
  participantJoinedLateMeetingCount,
  talkTimePercent,
  participantLatePercent,
  participantAttendance,
  participantMeetingScore,
  participantEngagement,
  participantSentiment,
  participantTimeInMeeting,
  participantHoursInMeeting,
  participantCharisma,
  participantBias,
  participantLateBy,
  avgParticipantLateBy,
  participantTalkTime,
  participantMissedMeetings,
  participantMissedMeetingsPercent,
  participantParticipation,
  observedParticipantCount,
  meetingDurationPlanned,
  meetingDurationActual,
  meetingStartedLateCount,
  meetingLateStartPercent,
  meetingWithScheduledDurationCount,
  meetingScore,
  sentiment,
  engagement,
  onTimeRate,
  participantObservedJoinTime,
  attendanceRate,
  numInstancesBySession,
  meetingTalkTimeEntropy,
  avgMeetingLateBy,
  meetingSizeBucket,
};

export type QueryMetric = keyof typeof METRIC_META_BY_ID;

export const getMetricMetaById = (
  id: QueryMetric,
  displayName?: string | number
): MetricMeta => {
  const meta = METRIC_META_BY_ID[id];
  if (!meta) {
    throw Error(`no metric by id ${id} found`);
  }
  if (displayName && typeof displayName === "string") {
    return { ...meta, displayName };
  }
  return meta;
};
